import { useCallback, useEffect, useRef, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { trackActivity } from '@/utils/activityTracker';
import { AuthContext } from './auth-context';

export { useAuth } from './auth-context';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const activityTracked = useRef(false);
  const initialSessionRestored = useRef(false);

  const applySession = useCallback((nextSession: Session | null) => {
    setSession(nextSession);
    setUser(nextSession?.user ?? null);

    // Track activity once per app load when a session exists. Fire-and-forget:
    // Supabase advises not to await extra auth calls inside auth callbacks.
    if (nextSession?.user && !activityTracked.current) {
      activityTracked.current = true;
      void trackActivity();
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    // Set up auth state listener first, but do not mark auth as ready until
    // getSession has restored any persisted/OAuth session from storage/URL.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (!mounted) return;
        applySession(session);
        if (initialSessionRestored.current) {
          setLoading(false);
        }
      }
    );

    // Then explicitly restore the existing session. Protected routes remain in
    // a loading state until this resolves, preventing a false redirect to /auth.
    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        if (!mounted) return;
        initialSessionRestored.current = true;
        applySession(session);
        setLoading(false);
      })
      .catch(() => {
        if (!mounted) return;
        initialSessionRestored.current = true;
        applySession(null);
        setLoading(false);
      });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [applySession]);

  const signIn = async (email: string, password: string, rememberMe: boolean = false) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    // Record login timestamp on successful login
    if (!error && data?.user) {
      await supabase
        .from('profiles')
        .update({ last_login_at: new Date().toISOString() })
        .eq('id', data.user.id);
    }
    
    // Store remember me preference for future session management
    if (!error && rememberMe) {
      localStorage.setItem('rememberMe', 'true');
    } else {
      localStorage.removeItem('rememberMe');
    }
    
    return { error };
  };

  const signUp = async (email: string, password: string, firstName: string, lastName: string = '') => {
    try {
      const lastNameValue = lastName || '';
      const fullName = lastNameValue ? `${firstName} ${lastNameValue}`.trim() : firstName.trim();

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastNameValue,
            full_name: fullName,
          },
          emailRedirectTo: `${window.location.origin}/auth`,
        },
      });

      if (error) {
        return { error: { message: error.message } };
      }

      // If user exists but email not confirmed, signUp returns user with identities = []
      if (data?.user?.identities?.length === 0) {
        return { error: { message: 'An account with this email already exists. Please sign in instead.' } };
      }

      return { error: null };
    } catch (err: any) {
      return { error: { message: err.message || 'An unexpected error occurred' } };
    }
  };

  const signOut = async () => {
    try {
      // Use scope: 'local' to only clear local session if server logout fails
      await supabase.auth.signOut({ scope: 'local' });
    } catch (error) {
      // Even if server logout fails, clear local state
      console.error('Logout error:', error);
    }
    // Manually clear state to ensure logout happens even if server call fails
    setSession(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}


